import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { Kunde, Grab, CreateKundeRequest, UpdateKundeRequest } from '../types';
import kundeService from '../services/kundeService';
import grabService from '../services/grabService';
import AnredeSelect from './AnredeSelect';
import { Modal, Field, Input, Textarea, Button, FormActions, Icon } from './ui';
import { markiereNeu } from '../utils/ankunft';

const emptyForm = {
  anrede: '', vorname: '', nachname: '', strasse: '', hausnummer: '', plz: '', ort: '',
  telefon: '', mobil: '', email: '', notizen: '', active: true,
};

const fromKunde = (k: Kunde) => ({
  anrede: k.anrede || '', vorname: k.vorname, nachname: k.nachname,
  strasse: k.strasse || '', hausnummer: k.hausnummer || '', plz: k.plz || '', ort: k.ort || '',
  telefon: k.telefon || '', mobil: k.mobil || '', email: k.email || '',
  notizen: k.notizen || '', active: k.active,
});

/**
 * Anlegen/Bearbeiten eines Kunden samt Mehrfach-Zuordnung von Gräbern (unabhängig vom Vertrag).
 * Lädt die Gräberliste selbst und ruft nach dem Speichern `onSaved` auf. `kunde` = null → Anlegen.
 * `preselectGrabIds` setzt beim Anlegen vorausgewählte Gräber (z. B. Aufruf aus der Grab-Detailseite).
 */
const KundeFormModal: React.FC<{ kunde?: Kunde | null; preselectGrabIds?: number[]; onClose: () => void; onSaved: () => void }> = ({ kunde, preselectGrabIds, onClose, onSaved }) => {
  const editing = kunde ?? null;
  const [formData, setFormData] = useState<any>(editing ? fromKunde(editing) : emptyForm);
  const [graeber, setGraeber] = useState<Grab[]>([]);
  const [selectedGrabIds, setSelectedGrabIds] = useState<number[]>(editing ? [] : (preselectGrabIds ?? []));
  const [grabQuery, setGrabQuery] = useState('');
  const [saving, setSaving] = useState(false);
  // Gefundene mögliche Dubletten (gleicher Vor-/Nachname + PLZ) – nur beim Anlegen.
  const [dubletten, setDubletten] = useState<Kunde[] | null>(null);

  useEffect(() => {
    grabService.getAll()
      .then(g => {
        setGraeber(g);
        if (editing) setSelectedGrabIds(g.filter(x => x.kundeId === editing.id).map(x => x.id));
      })
      .catch(() => {/* Zuordnung optional */});
  }, [editing]);

  // Bei Änderung der dubletten-relevanten Felder die Warnung verwerfen → erneute Prüfung beim Speichern.
  useEffect(() => { setDubletten(null); }, [formData.vorname, formData.nachname, formData.plz]);

  const toggleGrab = (id: number) =>
    setSelectedGrabIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // Gräber für die Auswahl-Liste: nach Suchbegriff gefiltert, ausgewählte zuerst.
  const grabOptions = useMemo(() => {
    const q = grabQuery.trim().toLowerCase();
    const matches = graeber.filter(g => {
      if (!q) return true;
      return `${g.friedhofName ?? ''} ${g.abteilung}/${g.grabnummer}`.toLowerCase().includes(q);
    });
    return matches.sort((a, b) => {
      const sa = selectedGrabIds.includes(a.id) ? 0 : 1;
      const sb = selectedGrabIds.includes(b.id) ? 0 : 1;
      return sa - sb
        || (a.friedhofName ?? '').localeCompare(b.friedhofName ?? '')
        || a.abteilung.localeCompare(b.abteilung, undefined, { numeric: true })
        || a.grabnummer.localeCompare(b.grabnummer, undefined, { numeric: true });
    });
  }, [graeber, grabQuery, selectedGrabIds]);

  // Speichert tatsächlich (legt an bzw. aktualisiert) – ohne weitere Dubletten-Prüfung.
  const doSave = async () => {
    setSaving(true);
    try {
      const data: any = { ...formData };
      Object.keys(data).forEach(k => { if (data[k] === '') data[k] = undefined; });
      data.vorname = formData.vorname; data.nachname = formData.nachname;
      let kundeId: number;
      if (editing) {
        await kundeService.update(editing.id, { ...data, active: formData.active } as UpdateKundeRequest);
        kundeId = editing.id;
        toast.success('Kunde gespeichert');
      } else {
        const created = await kundeService.create(data as CreateKundeRequest);
        kundeId = created.id;
        toast.success('Kunde angelegt');
      }
      await kundeService.setGraeber(kundeId, selectedGrabIds);
      markiereNeu(`kunde-${kundeId}`);
      onSaved();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Kunde konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Beim Anlegen vor dem Speichern auf mögliche Dubletten prüfen (Warnung, kein harter Block).
    if (!editing) {
      setSaving(true);
      try {
        const treffer = await kundeService.findDuplikate(formData.vorname.trim(), formData.nachname.trim(), formData.plz.trim() || undefined);
        if (treffer.length > 0) { setDubletten(treffer); setSaving(false); return; }
      } catch {
        /* Prüfung optional – bei Fehler normal weiter anlegen */
      }
      setSaving(false);
    }
    doSave();
  };

  return (
    <Modal title={editing ? `Kunde bearbeiten (${editing.kundennummer})` : 'Neuer Kunde'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <Field label="Anrede"><AnredeSelect value={formData.anrede} onChange={anrede => setFormData({ ...formData, anrede })} /></Field>
          <Field label="Nachname" required><Input required value={formData.nachname} onChange={e => setFormData({ ...formData, nachname: e.target.value })} /></Field>
          <Field label="Vorname" required><Input required value={formData.vorname} onChange={e => setFormData({ ...formData, vorname: e.target.value })} /></Field>
        </div>
        <div className="grid grid-cols-4 gap-4">
          <Field label="Straße" className="col-span-3"><Input value={formData.strasse} onChange={e => setFormData({ ...formData, strasse: e.target.value })} /></Field>
          <Field label="Nr."><Input value={formData.hausnummer} onChange={e => setFormData({ ...formData, hausnummer: e.target.value })} /></Field>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Field label="PLZ"><Input value={formData.plz} onChange={e => setFormData({ ...formData, plz: e.target.value })} /></Field>
          <Field label="Ort" className="col-span-2"><Input value={formData.ort} onChange={e => setFormData({ ...formData, ort: e.target.value })} /></Field>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Telefon"><Input value={formData.telefon} onChange={e => setFormData({ ...formData, telefon: e.target.value })} /></Field>
          <Field label="Mobil"><Input value={formData.mobil} onChange={e => setFormData({ ...formData, mobil: e.target.value })} /></Field>
          <Field label="E-Mail"><Input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} /></Field>
        </div>
        <Field label="Notizen"><Textarea rows={2} value={formData.notizen} onChange={e => setFormData({ ...formData, notizen: e.target.value })} /></Field>

        {/* Zugeordnete Gräber (Mehrfachauswahl, unabhängig vom Vertrag) */}
        <Field label={`Zugeordnete Gräber${selectedGrabIds.length ? ` (${selectedGrabIds.length})` : ''}`} hint="Optional – ein Kunde kann mehrere Gräber haben">
          <div className="rounded-lg border border-gray-300">
            <div className="relative border-b border-gray-200">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"><Icon.Search className="h-4 w-4" /></span>
              <input
                type="text"
                value={grabQuery}
                onChange={e => setGrabQuery(e.target.value)}
                placeholder="Grab suchen (Friedhof, Abteilung, Nummer)…"
                className="w-full rounded-t-lg border-0 py-2 pl-9 pr-3 text-sm focus:ring-0"
              />
            </div>
            <div className="max-h-44 overflow-y-auto p-1">
              {grabOptions.length === 0 ? (
                <p className="px-2 py-3 text-sm text-gray-400">{grabQuery.trim() ? 'Keine Treffer für diese Suche.' : 'Noch keine Gräber angelegt.'}</p>
              ) : grabOptions.map(g => {
                const checked = selectedGrabIds.includes(g.id);
                const fremd = g.kundeId != null && g.kundeId !== editing?.id;
                return (
                  <label key={g.id} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-gray-50">
                    <input type="checkbox" checked={checked} onChange={() => toggleGrab(g.id)} className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                    <span className="text-gray-900">{g.friedhofName ? `${g.friedhofName} · ` : ''}{g.abteilung}/{g.grabnummer}</span>
                    {fremd && <span className="ml-auto text-xs text-amber-600" title="Aktuell einem anderen Kunden zugeordnet">→ {g.kundeName}</span>}
                  </label>
                );
              })}
            </div>
          </div>
        </Field>

        {editing && (
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={formData.active} onChange={e => setFormData({ ...formData, active: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
            Aktiv
          </label>
        )}

        {dubletten && dubletten.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <p className="font-semibold">Möglicher doppelter Kunde</p>
            <p className="mt-0.5 text-amber-800">
              {dubletten.length === 1 ? 'Es existiert bereits ein Kunde' : `Es existieren bereits ${dubletten.length} Kunden`} mit demselben Namen und derselben PLZ:
            </p>
            <ul className="mt-2 space-y-1">
              {dubletten.map(k => (
                <li key={k.id} className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-medium">{k.vorname} {k.nachname}</span>
                  <span className="text-amber-700">{k.kundennummer}</span>
                  {(k.strasse || k.ort) && <span className="text-amber-700">· {[[k.strasse, k.hausnummer].filter(Boolean).join(' '), [k.plz, k.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</span>}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-amber-800">Trotzdem einen neuen Kunden anlegen?</p>
          </div>
        )}

        <FormActions>
          <Button type="button" variant="secondary" onClick={onClose}>Abbrechen</Button>
          {dubletten && dubletten.length > 0 ? (
            <Button type="button" loading={saving} onClick={() => { setDubletten(null); doSave(); }}>Trotzdem anlegen</Button>
          ) : (
            <Button type="submit" loading={saving}>{editing ? 'Speichern' : 'Anlegen'}</Button>
          )}
        </FormActions>
      </form>
    </Modal>
  );
};

export default KundeFormModal;
